import { Heading, Text } from '@react-spectrum/s2';
import ChevronRight from '@react-spectrum/s2/icons/ChevronRight';
import {
	iconStyle,
	style,
} from '@react-spectrum/s2/style' with { type: 'macro' };
import { useCallback, useState } from 'react';

import { useBridgeApi } from '@/api/BridgeApi';
import { useBridgewayApi } from '@/api/BridgewayApi.ts';
import { useUserId } from '@/auth/UserIdContext';
import { AnimateIn } from '@/components/AnimateIn';
import { LoadingInterstitial } from '@/components/LoadingInterstitial.tsx';
import { useBackToChat } from '@/hooks/useBackToChat';
import {
	useContentArtifacts,
	type RenderableContentArtifact,
} from '@/hooks/useContentArtifacts';
import { SpaceLayout } from '@/layouts/SpaceLayout';
import { logger } from '@/logger';
import { cn } from '@/utils/cn';
import { trackEvent } from '@/utils/fullstory';

import type { InspirationReference } from '../../../../bridge/types/artifact.ts';
import type { ContentSpace as ContentSpaceState } from '../../../../bridge/types/state.ts';
import type { ActiveFilter } from '../campaigns/campaignLibraryFilterUtils';

import { ContentDetail } from './ContentDetail';
import {
	closeChatPanelForArtifacts,
	trackArtifactDeleted,
} from './contentDetailUtils';
import { ContentLibrary } from './ContentLibrary';
import { ContentTabsHeader, type ContentTab } from './ContentTabsHeader';
import { InspirationDetail } from './InspirationDetail';
import { InspirationLibrary } from './InspirationLibrary';

const headingStyles = style({
	font: 'heading',
	fontWeight: 'bold',
	fontSize: 'heading-lg',
});

const artifactTitleStyles = style({
	font: 'heading',
	fontWeight: 'medium',
	fontSize: 'heading',
});

const crumbLinkStyles = style({
	font: 'heading',
	fontWeight: 'bold',
	fontSize: 'heading-lg',
});

const separatorIconStyles = iconStyle({ size: 'XL', color: 'neutral' });

const errorTextStyles = style({
	font: 'body',
	fontSize: 'body-sm',
	color: 'negative',
});

const getArtifactTitle = (artifact: RenderableContentArtifact): string => {
	switch (artifact.type) {
		case 'instagram_post':
			return (
				artifact.content.caption?.split('\n')[0]?.slice(0, 80) ||
				'Instagram post'
			);
		case 'digital_ad':
			return artifact.content.headline || 'Untitled ad';
		case 'email_html':
			return (
				artifact.content.subject || artifact.content.name || 'Untitled email'
			);
		case 'email_plaintext':
			return artifact.content.subject || 'Untitled email';
	}
};

export const ContentSpace = function ContentSpace() {
	const bridgeApi = useBridgeApi();
	const bridgewayApi = useBridgewayApi();
	const userId = useUserId();
	const {
		content,
		inspirations,
		processById,
		emailActionByRef,
		isLoading,
		refresh,
	} = useContentArtifacts();
	// Opening or deleting a draft swaps the library out for the detail view, which
	// unmounts it. Keeping the filters up here is what stops them resetting.
	const [filters, setFilters] = useState<ActiveFilter[]>([]);
	const [bulkDeleteError, setBulkDeleteError] = useState<string | null>(null);
	// Local, like the filters above: opening a detail view unmounts the library,
	// and the customer should come back to the tab they left.
	const [activeTab, setActiveTab] = useState<ContentTab>('content');
	const currentSpace = bridgeApi.user_state?.current_space;
	const selectedArtifactId =
		currentSpace?.type === 'content'
			? ((currentSpace as ContentSpaceState).artifact_id ?? null)
			: null;

	const closeDetail = () => {
		bridgeApi.sendAction({
			type: 'do_navigate_to_content_detail_page',
			artifact_id: null,
		});
		refresh();
	};

	const handleClickContent = (clickedContent: RenderableContentArtifact) => {
		trackEvent('Content Post Viewed', { post_id: clickedContent.id });
		bridgeApi.sendAction({
			type: 'do_navigate_to_content_detail_page',
			artifact_id: clickedContent.id,
		});
	};

	const handleBack = useBackToChat();

	const handleClickInspiration = (artifact: InspirationReference) => {
		trackEvent('Inspiration Viewed', { artifact_id: artifact.id });
		setActiveTab('inspiration');
		bridgeApi.sendAction({
			type: 'do_navigate_to_content_detail_page',
			artifact_id: artifact.id,
		});
	};

	const handleBulkDelete = useCallback(
		async (ids: string[]) => {
			if (!userId || ids.length === 0) return;
			setBulkDeleteError(null);
			// One patch per draft, the same call the detail view makes. allSettled so
			// one failure does not abandon the drafts after it in the list.
			const results = await Promise.allSettled(
				ids.map((id) =>
					bridgewayApi.patch(`/api/user/${userId}/artifacts/${id}`, {
						status: 'cancelled',
					}),
				),
			);

			const failedIds: string[] = [];
			results.forEach((result, index) => {
				const id = ids[index];
				if (!id) return;
				if (result.status === 'fulfilled') {
					const artifact = content.find((a) => a.id === id);
					if (artifact) trackArtifactDeleted(artifact);
				} else {
					failedIds.push(id);
					logger.error('Failed to delete draft', result.reason);
				}
			});

			if (failedIds.length > 0) {
				const noun = failedIds.length === 1 ? 'draft' : 'drafts';
				setBulkDeleteError(
					`Failed to delete ${failedIds.length} ${noun}. Please try again.`,
				);
			}

			await refresh();
			closeChatPanelForArtifacts(
				bridgeApi,
				ids.filter((id) => !failedIds.includes(id)),
			);
		},
		[bridgeApi, bridgewayApi, content, refresh, userId],
	);

	if (isLoading) {
		return (
			<SpaceLayout title="Content" onBack={handleBack} fillHeight>
				<LoadingInterstitial label="Loading Content" />
			</SpaceLayout>
		);
	}

	const selectedArtifact = selectedArtifactId
		? (content.find((artifact) => artifact.id === selectedArtifactId) ?? null)
		: null;

	const selectedInspiration = selectedArtifactId
		? (inspirations.find((a) => a.id === selectedArtifactId) ?? null)
		: null;

	const header = (
		<AnimateIn delay={0}>
			<nav aria-label="Breadcrumb" className="flex min-w-0 items-center gap-1">
				{selectedArtifactId ? (
					<>
						<button
							type="button"
							onClick={closeDetail}
							className={cn(
								`shrink-0 cursor-pointer text-(--text-secondary) decoration-2
									underline-offset-4 transition-colors
									hover:text-(--text-primary) hover:underline`,
								crumbLinkStyles,
							)}
						>
							Content
						</button>
						<ChevronRight
							styles={separatorIconStyles}
							UNSAFE_className="shrink-0"
							aria-hidden
						/>
						<Heading
							level={1}
							styles={artifactTitleStyles}
							UNSAFE_className="min-w-0 truncate"
						>
							{selectedInspiration
								? selectedInspiration.content.topic || 'Saved inspiration'
								: selectedArtifact
									? getArtifactTitle(selectedArtifact)
									: 'Not found'}
						</Heading>
					</>
				) : (
					<Heading level={1} styles={headingStyles}>
						Content
					</Heading>
				)}
			</nav>
		</AnimateIn>
	);

	// Detail keeps the breadcrumb header and its own padding. The library view
	// is the tabbed one: a shrink-0 header band, then a panel that owns the
	// scroll — matching PeopleSpace, where the header never moves.
	if (selectedArtifactId) {
		return (
			<SpaceLayout title="Content" onBack={handleBack} fillHeight>
				<div className="flex h-full min-h-0 flex-col gap-6 pt-6">
					{header}
					{selectedInspiration ? (
						<InspirationDetail
							artifact={selectedInspiration}
							refresh={refresh}
						/>
					) : selectedArtifact ? (
						<ContentDetail
							artifact={selectedArtifact}
							processById={processById}
							emailActionByRef={emailActionByRef}
							refresh={refresh}
						/>
					) : (
						<div className="flex flex-1 items-center justify-center p-4">
							<p className="text-(--text-primary)">Content not found.</p>
						</div>
					)}
				</div>
			</SpaceLayout>
		);
	}

	return (
		<SpaceLayout title="Content" onBack={handleBack} fillHeight>
			<div className="flex h-full min-h-0 flex-col">
				<ContentTabsHeader activeTab={activeTab} onTabChange={setActiveTab} />
				<div className="flex min-h-0 min-w-0 flex-1 flex-col px-6 pb-6">
					{bulkDeleteError && activeTab === 'content' && (
						<div data-testid="content-bulk-error" className="pb-6">
							<Text styles={errorTextStyles}>{bulkDeleteError}</Text>
						</div>
					)}
					<div
						className={cn(
							'flex min-h-0 min-w-0 flex-1 flex-col',
							activeTab !== 'content' && 'hidden',
						)}
					>
						<ContentLibrary
							content={content}
							processById={processById}
							emailActionByRef={emailActionByRef}
							filters={filters}
							onFiltersChange={setFilters}
							onBulkDelete={handleBulkDelete}
							onClickContent={handleClickContent}
						/>
					</div>
					<div
						className={cn(
							'flex min-h-0 min-w-0 flex-1 flex-col',
							activeTab !== 'inspiration' && 'hidden',
						)}
					>
						<InspirationLibrary
							inspirations={inspirations}
							onClickInspiration={handleClickInspiration}
						/>
					</div>
				</div>
			</div>
		</SpaceLayout>
	);
};
